import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterTypes } from '../../../../../interfaces/src/lib/enums/index';
import {
  FilterOptionSliceType,
  GenericFilterState,
} from '../../../../../interfaces/src/lib/types/index';
import { getQueryParamIgnoreCase } from '../../utilFuncs/utilFuncs';
import { WritableDraft } from 'immer';

interface QueryParams {
  numberOfNights?: string | null;
  promotion?: string | null;
  tag?: string | null;
  flyAndCruise?: string | null;
  pastBackup: {
    numberOfNights?: FilterOptionSliceType[];
    promotion?: FilterOptionSliceType[];
    tag?: FilterOptionSliceType[];
    flyAndCruise?: FilterOptionSliceType[];
  };
}

function parseQueryInit(): {
  selectedNumberOfNights: FilterOptionSliceType[];
  selectedPromotion: FilterOptionSliceType[];
  selectedTag: FilterOptionSliceType[];
  selectedFlyAndCruise: FilterOptionSliceType[];
} {
  const queryParams: QueryParams = {
    numberOfNights: getQueryParamIgnoreCase(window.location.search, 'nights'),
    promotion: getQueryParamIgnoreCase(window.location.search, 'paxType'),
    tag: getQueryParamIgnoreCase(window.location.search, 'tag'),
    flyAndCruise: getQueryParamIgnoreCase(window.location.search, 'flyAndCruise'),
    pastBackup: {},
  };

  const parseValues = (paramValue: string | null | undefined) => {
    if (!paramValue) return [];
    return paramValue.split(',').map((item) => ({
      name: '',
      value: item.trim(),
      isFromURL: true,
      isOnlyURL: true,
      useInSearch: true,
    }));
  };

  const selectedNumberOfNights: FilterOptionSliceType[] = queryParams.numberOfNights
    ? [
        {
          name: 'urlNights',
          value: queryParams.numberOfNights.trim(),
          isFromURL: true,
          isOnlyURL: true,
          useInSearch: true,
        },
      ]
    : [];
  const selectedPromotion: FilterOptionSliceType[] = parseValues(queryParams.promotion);
  const selectedTag: FilterOptionSliceType[] = parseValues(queryParams.tag);
  const selectedFlyAndCruise: FilterOptionSliceType[] = parseValues(queryParams.flyAndCruise);
  return { selectedNumberOfNights, selectedPromotion, selectedTag, selectedFlyAndCruise };
}

const initialState: GenericFilterState = {
  hasAir: false,
  numberOfNights: parseQueryInit().selectedNumberOfNights,
  promotion: parseQueryInit().selectedPromotion,
  tag: parseQueryInit().selectedTag,
  flyAndCruise: parseQueryInit().selectedFlyAndCruise,
  pastBackup: {},
};

const genericFilterSlice = createSlice({
  name: 'genericFilter',
  initialState,
  reducers: {
    resetGenericBackup: (state: GenericFilterState) => {
      state.pastBackup = {};
    },
    restoreGenericState: (state) => {
      if (Object.keys(state.pastBackup).length === 0) return;
      if (state.pastBackup.numberOfNights) state.numberOfNights = state.pastBackup.numberOfNights;
      if (state.pastBackup.promotion) state.promotion = state.pastBackup.promotion;
      if (state.pastBackup.tag) state.tag = state.pastBackup.tag;
      if (state.pastBackup.flyAndCruise) state.flyAndCruise = state.pastBackup.flyAndCruise;
      state.pastBackup = {};
    },
    setGenericBackup: (
      state,
      action: PayloadAction<{
        filterType: string;
        filterValue: WritableDraft<FilterOptionSliceType>[] | undefined;
      }>
    ) => {
      switch (action.payload.filterType) {
        case FilterTypes.NUMBER_OF_NIGHTS:
          if (!state?.pastBackup?.numberOfNights)
            state.pastBackup.numberOfNights = action.payload.filterValue;
          break;
        case FilterTypes.TAG:
          if (!state?.pastBackup?.tag) state.pastBackup.tag = action.payload.filterValue;
          break;
        case FilterTypes.PROMOTION:
          if (!state?.pastBackup?.promotion)
            state.pastBackup.promotion = action.payload.filterValue;
          break;
        case FilterTypes.FLY_AND_CRUISE:
          if (!state?.pastBackup?.flyAndCruise)
            state.pastBackup.flyAndCruise = action.payload.filterValue;
          break;
      }
    },
    setNumberOfNights: (
      state: GenericFilterState,
      action: PayloadAction<FilterOptionSliceType[]>
    ) => {
      state.numberOfNights = action.payload;
      return state;
    },
    setPromotions: (state: GenericFilterState, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.promotion = action.payload;
      return state;
    },
    setTags: (state: GenericFilterState, action: PayloadAction<FilterOptionSliceType[]>) => {
      state.tag = action.payload;
      return state;
    },
    setFlyAndCruise: (
      state: GenericFilterState,
      action: PayloadAction<FilterOptionSliceType[]>
    ) => {
      state.flyAndCruise = action.payload;
      return state;
    },
    setHasAir: (state: GenericFilterState, action: PayloadAction<boolean>) => {
      state.hasAir = action.payload;
      return state;
    },
    resetNumberOfNightsFilters: (state) => {
      state.numberOfNights = [];
    },
    resetPromotionFilters: (state) => {
      state.promotion = [];
    },
    resetTagFilters: (state) => {
      state.tag = [];
    },
    resetFlyAndCruiseFilters: (state) => {
      state.flyAndCruise = [];
    },
    cleanGenericFiltersNotUsedInSearch: (state: GenericFilterState) => {
      state.numberOfNights = state.numberOfNights.filter((option) => option.useInSearch);
      state.promotion = state.promotion.filter((option) => option.useInSearch);
      state.tag = state.tag.filter((option) => option.useInSearch);
      state.flyAndCruise = state.flyAndCruise.filter((option) => option.useInSearch);
      return state;
    },
    verifyGenericFiltersNotUsedForSearch: (state: GenericFilterState) => {
      state.numberOfNights = state.numberOfNights.map((selectedOption) => ({
        ...selectedOption,
        useInSearch: true,
      }));
      state.promotion = state.promotion.map((selectedOption) => ({
        ...selectedOption,
        useInSearch: true,
      }));
      state.tag = state.tag.map((selectedOption) => ({
        ...selectedOption,
        useInSearch: true,
      }));
      state.flyAndCruise = state.flyAndCruise.map((selectedOption) => ({
        ...selectedOption,
        useInSearch: true,
      }));
      return state;
    },
  },
});

export const {
  setNumberOfNights,
  setPromotions,
  setTags,
  setFlyAndCruise,
  setHasAir,
  resetNumberOfNightsFilters,
  resetPromotionFilters,
  resetTagFilters,
  resetFlyAndCruiseFilters,
  resetGenericBackup,
  restoreGenericState,
  setGenericBackup,
  cleanGenericFiltersNotUsedInSearch,
  verifyGenericFiltersNotUsedForSearch,
} = genericFilterSlice.actions;
export default genericFilterSlice.reducer;
